import {
  ErrorMembersType,
  LookDataType,
  LookItemType,
  LookProductsType,
  LooksResponseDataType,
  LooksResponseErrorType,
  LooksResponseType,
  TagKeyToStringsObject,
  TagType,
} from "./LookTypes";
import * as Sentry from "@sentry/browser";
import {getShopId} from "../../lib/getShopId";
import {Customer, IItemContent} from "../../../typescript-library";
import {BrowserStorage} from "../../../storage/BrowserStorage";
import {LookStateType} from "./LookOverview";

type ApiResultType = {result: IItemContent[]};

export const queryBreakpointSmall = "(max-width: 680px)";
export const queryBreakpointMedium = "(max-width: 767px)";
export const queryBreakpointLarge = "(max-width: 940px)";

export const looksApiUrl = `https://api.stl${__STAGING__ ? "-staging" : ""}.connox.io/api/shopthelook/looks`;
const limit = "200";

export const lookListStorage = new BrowserStorage<LooksResponseDataType>({
  type: "localStorage",
  dataIdentifier: "look-list",
  hoursToCache: 18,
});

const lookProductStorage = new BrowserStorage<LookItemType[]>({
  type: "localStorage",
  dataIdentifier: "look-shop-products",
  hoursToCache: 1,
});

const productRelatedLooksStorage = new BrowserStorage<LooksResponseDataType>({
  type: "localStorage",
  dataIdentifier: "looks-with-product",
  hoursToCache: 18,
});

const headers: HeadersInit = {
  "Content-Type": "application/json",
};

const lookApiHeaders = {
  ...headers,
  "X-Api-Key": "odlhfglseoditvne4p8n",
};

const writeToStorage = async (
  storageKey: string,
  looksData: LooksResponseDataType,
  useProductRelatedLooksStorage: boolean = false,
) => {
  await new Promise<void>((resolve) => {
    if (useProductRelatedLooksStorage) {
      productRelatedLooksStorage.write(storageKey, looksData);
    } else {
      lookListStorage.write(storageKey, looksData);
    }
    resolve();
  });
};

export const fetchLook = async (lookId: string | null) => {
  const storageKey = `limit-${limit}`;
  let looksData = lookListStorage.read(storageKey);

  /* eslint-disable no-underscore-dangle */
  if (!looksData || (lookId && !looksData.looks.some((ldt) => ldt._id === lookId))) {
    looksData = await fetchLooks(`${looksApiUrl}?limit=${limit}`);
    void writeToStorage(storageKey, looksData);
  }

  if (!lookId) {
    return looksData.looks[0];
  }

  return looksData.looks.find((look) => look._id === lookId);
  /* eslint-enable no-underscore-dangle */
};

export const fetchTagRelatedLooks = async (tagString: string) => {
  const storageKey = `taglist-${tagString}`;
  const storageLooks = lookListStorage.read(storageKey);

  let looksData: LooksResponseDataType;
  if (storageLooks) {
    looksData = storageLooks;
  } else {
    const usp = new URLSearchParams({taglist: tagString});
    usp.set("limit", "32");
    looksData = await fetchLooks(looksApiUrl + `?${usp.toString()}`);

    void writeToStorage(storageKey, looksData);
  }

  return looksData.looks;
};

export const fetchProductRelatedLooks = async (itemId: string) => {
  const storageKey = `${itemId}`;
  const storageLooks = productRelatedLooksStorage.read(storageKey);

  let looksData: LooksResponseDataType;
  if (storageLooks) {
    looksData = storageLooks;
  } else {
    looksData = await fetchLooks(`${looksApiUrl}/product/${itemId}`);

    void writeToStorage(storageKey, looksData, true);
  }

  return looksData;
};

export const fetchLooks = async (apiUrl: string) => {
  return await fetch(apiUrl, {
    headers: lookApiHeaders,
  })
    .then(async (response) => {
      const responseJson = (await response.json()) as LooksResponseType;
      if (!response.ok && isLookError(responseJson)) {
        return Promise.reject(
          new ResponseError<LooksResponseErrorType>("Error occurred when retrieving Looks", "LooksError", {
            status: response.status,
            statusText: response.statusText,
            response: responseJson,
          }),
        );
      }

      return responseJson as LooksResponseDataType;
    })
    .catch((err: ResponseError<LooksResponseErrorType>) => {
      Sentry.captureException(err, {
        extra: {
          data: err.data || {},
        },
      });

      throw err;
    });
};

export const fetchProductsFromInternalApi = async (look: LookDataType, customer: Customer): Promise<LookItemType[]> => {
  const itemIdSet = new Set(look.products.map((product) => product.productId));
  const itemString = Array.from(itemIdSet).join(",");
  const {customerGroupId, customerId} = customer;
  const customerParams = [`currencyCode=${window.c_ActiveCurrency}`];

  if (customerId) {
    customerParams.push(`customerId=${customerId}`);
  }

  if (customerGroupId) {
    customerParams.push(`customerGroupId=${customerGroupId}`);
  }

  const customerParamsString = customerParams.join("&");

  const paramsConcat = `itemIds=${itemString}&${customerParamsString}`;
  // eslint-disable-next-line no-underscore-dangle
  const storageData = lookProductStorage.read(`${look._id}-${paramsConcat}`);
  if (storageData) {
    return storageData;
  }

  const internalUrl = `${__API_URL__}/shop/${getShopId()}/items?view=content&${paramsConcat}`;

  if (customer.token) {
    headers.Authorization = `Bearer ${customer.token}`;
  }

  const {result} = await fetch(internalUrl, {
    method: "GET",
    headers,
  }).then((response) => response.json() as Promise<ApiResultType>);

  const lookProducts = [...look.products];
  const productsTotal = lookProducts.length;
  const newLookItems: LookItemType[] = [];
  const orphanedLookProducts: LookProductsType[] = [];

  lookProducts.forEach((lookProduct) => {
    const internalItem = result.find((item) => {
      return item.itemId.toString() === lookProduct.productId;
    });
    if (internalItem) {
      const imageSizes = internalItem.images.find((img) => img.mediaTypeId === 100016)?.sizes;
      newLookItems.push({
        ...lookProduct,
        item: internalItem,
        itemName: internalItem.title.split(/\s[-\u{2013}]\s/u, 2)[1],
        imgUrl1x: imageSizes?.find((size) => size.name === "thumbnail-2x")?.url,
        imgUrl2x: imageSizes?.find((size) => size.name === "half-2x")?.url,
      });
      if (__DEVELOPMENT__) {
        newLookItems[newLookItems.length - 1].imgUrl1x = newLookItems[newLookItems.length - 1].imgUrl1x?.replace(
          "www.",
          "cdn.",
        );
        newLookItems[newLookItems.length - 1].imgUrl2x = newLookItems[newLookItems.length - 1].imgUrl2x?.replace(
          "www.",
          "cdn.",
        );
      }
    } else {
      orphanedLookProducts.push(lookProduct);
    }
  });

  if (orphanedLookProducts.length) {
    // eslint-disable-next-line no-console
    console.log(
      `DEVELOP: ${orphanedLookProducts.length} of ${productsTotal} look products have no matching internal item data, for '${look.title}'`,
      orphanedLookProducts,
    );
  }

  // eslint-disable-next-line no-underscore-dangle
  lookProductStorage.write(`${look._id}-${paramsConcat}`, newLookItems);

  const sortedArray = newLookItems.reduce<LookItemType[][]>(
    (accumulator, current) => {
      accumulator[current.isAlternative ? 1 : 0].push(current);

      return accumulator;
    },
    [[], []],
  );

  return [...sortedArray[0], ...sortedArray[1]];
};

export class ResponseError<T = undefined> extends Error {
  public data: ErrorMembersType<T> | undefined;

  constructor(message: string, name?: string, data?: ErrorMembersType<T>) {
    super(message);
    if (name) {
      this.name = name.endsWith("Error") ? name : `${name}Error`;
    }

    this.data = data;
  }
}

export const isLookError = (value: LooksResponseType): value is LooksResponseErrorType => {
  return !!(value as LooksResponseErrorType).error || !(value as LooksResponseDataType).looks;
};

export const getTagObject = (allTags: TagType[]) => {
  const obj: TagKeyToStringsObject = {};

  for (const tag of allTags) {
    const [namespace, value] = tag.name.split(":");
    if (!namespace.endsWith("free") && !namespace.endsWith("hidden")) {
      obj[namespace] = [...(obj[namespace] || []), {tag, value, isSet: false, wasSet: false}];
    }
  }

  return obj;
};

// Return whole tags object (clone), with the changed ones added/deleted
export const updateTagsToApply = (
  tagKey: string,
  completeName: string,
  checked: boolean,
  looksData: LookStateType,
): TagKeyToStringsObject => {
  return {
    ...looksData.appliedTags,
    [tagKey]: looksData.appliedTags[tagKey].map((at) =>
      at.tag.name === completeName ? {...at, isSet: checked} : {...at},
    ),
  };
};

export const handleResponse = (looksResponse: LooksResponseDataType) => {
  const allTagsMap = getTagObject(looksResponse.allTags);
  const appliedTags = {...allTagsMap};

  Object.keys(allTagsMap).forEach((key) => {
    appliedTags[key] = appliedTags[key].map((tag) => {
      const isSet = looksResponse.appliedFilters.tags.includes(tag.tag.name);

      return {
        ...tag,
        isSet,
        wasSet: isSet,
      };
    });
  });

  return {
    looksApiResponse: looksResponse,
    allTagsMap,
    appliedTags,
    loading: false,
  };
};
